import { cloneElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { useResize } from '@hh.ru/magritte-common-resize';
import { BottomSheet } from '@hh.ru/magritte-ui-bottom-sheet';
import { BreadcrumbsDivider } from '@hh.ru/magritte-ui-breadcrumbs/BreadcrumbsDivider';
import type { BreadcrumbsItem, BreadcrumbsProps } from '@hh.ru/magritte-ui-breadcrumbs/types';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Card } from '@hh.ru/magritte-ui-card';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { Text, TextTypography } from '@hh.ru/magritte-ui-typography';

import styles from './breadcrumbs.less';

const MAX_VISIBLE_ITEMS = 5;
const TEXT_TYPOGRAPHY = 'label-3-regular';

const arrayFromRange = (start: number, end: number) => {
    return start <= end ? [...Array(end - start + 1).keys()].map((x) => x + start) : [];
};

const makeItemKey = (children: Array<BreadcrumbsItem>, index: number): React.Key => {
    return (
        children[index].key ??
        (typeof children[index].props.children === 'string' ? children[index].props.children : index)
    );
};

/**
 * В спецификации указано, что breadcrumbs не может отображать более 5-ти элементов.
 *
 * Варианты отображения breadcrumbs:
 * -> link / link / link / link / text
 * или
 * -> link / ellipsis / link / link / text
 *
 * Если лимит будет превышен, компонент схлопнет в ellipsis все элементы, которые окажутся между первым и
 * тремя последними элементами. Таким образом breadcrumbs будет приведен к виду -> link / ellipsis / link / link / text,
 * где ellipsis — это кнопка, раскрывающая дроп-контейнер, в который складываются сколлапсировавшие элементы.
 *
 * Если после лимитирования контейнер-родитель всё равно не будет умещать в себе контент, то видимые элементы
 * один за другим начнут коллапсировать до максимально возможного уровеня сворачивания элементов:
 * -> link / ellipsis / text.
 *
 * Если и после этого контент все равно будет слишком широким, первый элемент начнет уменьшаться с text-overflow:ellipsis.
 *
 * Коллапсирование реализовано стилями, layout effect только вычисляет, какие элементы нужно показывать в дроп-контейнере,
 * и прячет / показывает ellipsis с помощью `visibility:hidden`, не влияя на размеры чего-либо в верстке.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ children, 'data-qa': dataQa }) => {
    const activatorRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [firstCollapsedItemIndex, setFirstCollapsedItemIndex] = useState<number | null>(null);
    const [lastCollapsedItemIndex, setLastCollapsedItemIndex] = useState<number | null>(null);
    const showEllipsis = firstCollapsedItemIndex !== null && lastCollapsedItemIndex !== null;
    const [expandCollapsedItems, setExpandCollapsedItems] = useState(false);

    const itemRefs = useRef<Record<number, HTMLDivElement>>({});
    const itemRefCallbacks = useMemo<React.RefCallback<HTMLDivElement>[]>(
        () =>
            [...Array(children.length).keys()].map((_, index) => (element: HTMLDivElement) => {
                itemRefs.current[index] = element;
            }),
        [children.length]
    );

    const { isMobile } = useBreakpoint();

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const checkIfItemsCollapsed = useCallback(
        throttle(() => {
            const collapsedItemIndexes = (Object.keys(itemRefs.current) as unknown[] as number[]).filter(
                (index) => itemRefs.current[index]?.offsetTop > 0
            );

            const _firstCollapsedItemIndex = Math.min(
                ...collapsedItemIndexes,
                children.length > MAX_VISIBLE_ITEMS ? 1 : Infinity
            );

            const _lastCollapsedItemIndex = Math.max(
                ...collapsedItemIndexes,
                children.length > MAX_VISIBLE_ITEMS ? children.length - (MAX_VISIBLE_ITEMS - 1) : -Infinity
            );

            if (Number.isFinite(_firstCollapsedItemIndex) && Number.isFinite(_lastCollapsedItemIndex)) {
                setFirstCollapsedItemIndex(_firstCollapsedItemIndex);
                setLastCollapsedItemIndex(_lastCollapsedItemIndex);
            } else {
                setFirstCollapsedItemIndex(null);
                setLastCollapsedItemIndex(null);
                setExpandCollapsedItems(false);
            }
        }, 50),
        [children.length]
    );

    useLayoutEffect(checkIfItemsCollapsed);
    useResize(checkIfItemsCollapsed);

    const renderCollapsedItems = (itemClassName: string, typography: TextTypography) => {
        if (firstCollapsedItemIndex === null || lastCollapsedItemIndex === null) {
            return null;
        }

        return arrayFromRange(firstCollapsedItemIndex, Math.min(lastCollapsedItemIndex, children.length - 1)).map(
            (index) => {
                const { onClick, Element = 'a', children: child, ...props } = children[index].props;
                return (
                    <div className={itemClassName} key={makeItemKey(children, index)}>
                        <Card
                            {...props}
                            Element={Element}
                            borderRadius={12}
                            padding={8}
                            paddingLeft={12}
                            paddingRight={12}
                            style="blank"
                            hoverStyle="neutral"
                            actionCard
                            stretched
                        >
                            <div className={styles.interactiveText}>
                                <Text Element="div" typography={typography}>
                                    <div className={styles.textWithEllipsis}>{child}</div>
                                </Text>
                            </div>
                        </Card>
                    </div>
                );
            }
        );
    };

    const renderLastItem = (visible: boolean) => {
        if (children.length === 0) {
            return null;
        }

        const index = children.length - 1;
        const { children: child, href, ...props } = { ...children[index].props };
        return (
            <div
                className={classnames(styles.item, { [styles.itemAfterEllipsis]: index <= 1 })}
                key={makeItemKey(children, index)}
                ref={visible ? itemRefCallbacks[index] : undefined}
            >
                <div className={styles.staticText}>
                    <Text {...(visible ? props : {})} Element="span" typography={TEXT_TYPOGRAPHY}>
                        {child}
                    </Text>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.breadcrumbs} data-qa={dataQa} ref={containerRef}>
            <div className={styles.unbreakableContainer}>
                {/*
                    Нулевой айтем имеет text-overflow:ellipsis, все остальные скрываются целиком.
                    Он начнет уменьшаться, только если все остальные айтемы, кроме последнего, уже были скрыты.
                */}
                {children.length > 1 && (
                    <div className={classnames(styles.item, styles.firstItem)} ref={itemRefCallbacks[0]}>
                        <div className={styles.firstItemContent}>
                            {cloneElement(
                                children[0],
                                undefined,
                                <div className={styles.textWithEllipsis}>{children[0].props.children}</div>
                            )}
                        </div>
                        <BreadcrumbsDivider />
                    </div>
                )}

                {/*
                    Всегда рендерим эллипсис, если он не нужен, прячем его через visibility:hidden.
                    Если все айтемы поместились, айтем непосредственно после эллипсиса (его индекс min(length - 1, 1))
                    перекроет его за счет отрицательного margin-left.
                */}
                <div
                    className={classnames({ [styles.invisibleContainer]: !showEllipsis })}
                    data-qa="breadcrumbs-action"
                >
                    <button
                        type="button"
                        className={styles.ellipsis}
                        ref={activatorRef}
                        onClick={() => setExpandCollapsedItems(!expandCollapsedItems)}
                    >
                        <div className={styles.interactiveText}>
                            <Text Element="span" typography={TEXT_TYPOGRAPHY}>
                                ...
                            </Text>
                        </div>
                    </button>
                    <BreadcrumbsDivider />
                    {showEllipsis &&
                        (isMobile ? (
                            <BottomSheet visible={expandCollapsedItems} onClose={() => setExpandCollapsedItems(false)}>
                                {renderCollapsedItems(styles.bottomSheetItem, 'label-2-regular')}
                            </BottomSheet>
                        ) : (
                            <Drop
                                activatorRef={activatorRef}
                                placement="bottom-left"
                                space={300}
                                visible={expandCollapsedItems}
                                onClose={() => setExpandCollapsedItems(false)}
                            >
                                {renderCollapsedItems(styles.dropItem, TEXT_TYPOGRAPHY)}
                            </Drop>
                        ))}
                </div>

                {/*
                    Collapsible контейнер имеет минимальную ширину, равную ширине последнего айтема,
                    но стремится заполнить все доступное пространство.
                    При нехватке места айтемы, начиная с меньшего индекса, будут
                    переноситься на следующую строку флекс-контейнера и обрезаться overflow.
                    Последний айтем имеет наибольший приоритет перед всеми остальными и никогда не скрывается.
                */}
                <div className={styles.collapsibleContainer}>
                    {/*
                        Рендерим копию последнего айтема и прячем ее через visibility:hidden,
                        чтобы задать минимальную ширину collapsible контейнера
                    */}
                    <div className={styles.invisibleContainer}>{renderLastItem(false)}</div>

                    <div className={styles.collapsibleContent}>
                        {renderLastItem(true)}

                        {arrayFromRange(
                            children.length <= MAX_VISIBLE_ITEMS ? 1 : children.length - (MAX_VISIBLE_ITEMS - 2),
                            children.length - 2
                        )
                            .reverse()
                            .map((index) => (
                                <div
                                    className={classnames(styles.item, { [styles.itemAfterEllipsis]: index <= 1 })}
                                    key={makeItemKey(children, index)}
                                    ref={itemRefCallbacks[index]}
                                >
                                    {children[index]}
                                    <BreadcrumbsDivider />
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
